import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import Logo from '../components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SMS_CONSENT_TEXT =
  'I agree to receive SMS messages from NexFlow Plumbing & Drain Services regarding ' +
  'appointment reminders, dispatch notifications, account updates, verification codes, ' +
  'and customer service communications. Message frequency varies. Message and data ' +
  'rates may apply. Reply STOP to opt out. Reply HELP for assistance. Consent is not ' +
  'a condition of purchase.';
const SMS_CONSENT_VERSION = 'v2-2026-06-05';

const Register = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    role: 'customer',
  });
  const [smsConsent, setSmsConsent] = useState(false); // SEPARATE from any T&Cs — unchecked by default
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/register`, {
        ...form,
        sms_consent: smsConsent,
        sms_consent_version: smsConsent ? SMS_CONSENT_VERSION : null,
      });
      // Push to verification step. If preview mode, code is in data.verification_code.
      navigate('/verify', {
        state: {
          email: form.email,
          role: form.role,
          pendingApproval: !!data.pending_approval,
          previewCode: data.verification_code || null,
          deliveryMode: data.delivery_mode,
          smsSent: !!data.sms_sent,
        },
      });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) setError(detail[0]?.msg || 'Invalid data');
      else setError(detail || err.message || 'Registration failed');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8 flex flex-col items-center">
          <Logo size="lg" />
          <p className="text-[#D4AF37] text-xs font-bold tracking-[0.3em] mt-4 mb-2">LOCAL. TRUSTED. RELIABLE.</p>
          <p className="text-[#A3A3A3]">Create your account</p>
        </div>

        <div className="bg-[#141414] border border-[#262626] rounded-md p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div data-testid="register-error" className="bg-[#7F1D1D] text-white p-3 rounded-md text-sm">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="name" className="text-white">Full Name</Label>
              <Input
                data-testid="register-name-input"
                id="name"
                type="text"
                value={form.name}
                onChange={(e) => update('name', e.target.value)}
                required
                className="bg-[#0A0A0A] border-[#262626] text-white focus:ring-1 focus:ring-[#D4AF37]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-white">Email</Label>
              <Input
                data-testid="register-email-input"
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => update('email', e.target.value)}
                required
                className="bg-[#0A0A0A] border-[#262626] text-white focus:ring-1 focus:ring-[#D4AF37]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-white">Cell Phone Number</Label>
              <Input
                data-testid="register-phone-input"
                id="phone"
                type="tel"
                inputMode="tel"
                placeholder="(614) 555-1212"
                value={form.phone}
                onChange={(e) => update('phone', e.target.value)}
                required
                className="bg-[#0A0A0A] border-[#262626] text-white focus:ring-1 focus:ring-[#D4AF37]"
              />
              <p className="text-[#525252] text-xs">Used for service updates and account notifications.</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white">Password</Label>
              <Input
                data-testid="register-password-input"
                id="password"
                type="password"
                value={form.password}
                onChange={(e) => update('password', e.target.value)}
                required
                minLength={8}
                className="bg-[#0A0A0A] border-[#262626] text-white focus:ring-1 focus:ring-[#D4AF37]"
              />
              <p className="text-[#525252] text-xs">At least 8 characters.</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="role" className="text-white">Account Type</Label>
              <Select value={form.role} onValueChange={(v) => update('role', v)}>
                <SelectTrigger data-testid="register-role-select" className="bg-[#0A0A0A] border-[#262626] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626]">
                  <SelectItem value="customer" className="text-white">Customer</SelectItem>
                  <SelectItem value="employee" className="text-white">Employee</SelectItem>
                  <SelectItem value="vip" className="text-white">VIP Referral Partner</SelectItem>
                </SelectContent>
              </Select>
              {(form.role === 'employee' || form.role === 'vip') && (
                <p data-testid="register-approval-notice" className="text-[#D4AF37] text-xs">
                  {form.role === 'employee' ? 'Employee' : 'VIP'} accounts require admin approval. After verifying your email, an admin will review and activate your account before you can sign in.
                </p>
              )}
            </div>

            {/* SEPARATE, STANDALONE SMS consent — NOT bundled with T&Cs. Unchecked by default. */}
            <div className="space-y-2 pt-2 border-t border-[#262626]">
              <p className="text-[#D4AF37] text-[10px] font-bold tracking-[0.3em]">OPTIONAL — SMS COMMUNICATIONS</p>
              <label className="flex items-start gap-3 p-3 rounded-md border border-[#262626] hover:border-[#D4AF37]/50 transition-colors cursor-pointer">
                <input
                  data-testid="register-sms-consent-checkbox"
                  type="checkbox"
                  checked={smsConsent}
                  onChange={(e) => setSmsConsent(e.target.checked)}
                  className="mt-1 accent-[#D4AF37] w-4 h-4 shrink-0"
                />
                <span data-testid="register-sms-consent-text" className="text-[#A3A3A3] text-xs leading-relaxed">
                  {SMS_CONSENT_TEXT}
                </span>
              </label>
              <p className="text-[#525252] text-[11px]">
                This is separate from any agreement to our{' '}
                <Link to="/terms" target="_blank" className="text-[#A3A3A3] hover:text-[#D4AF37] underline">Terms</Link>{' '}or{' '}
                <Link to="/privacy" target="_blank" className="text-[#A3A3A3] hover:text-[#D4AF37] underline">Privacy Policy</Link>.
                Leaving it unchecked is fine — you can still create the account and opt in later at{' '}
                <Link to="/sms-consent" target="_blank" className="text-[#A3A3A3] hover:text-[#D4AF37] underline">/sms-consent</Link>.
              </p>
            </div>

            <Button
              data-testid="register-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-medium transition-all duration-200"
            >
              {loading ? 'Creating account…' : 'Create Account'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-[#A3A3A3] text-sm">
              Already have an account?{' '}
              <Link to="/login" className="text-[#D4AF37] hover:text-[#F3E5AB]">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
